import * as THREE from 'three';
import type { Bullet } from '../entities/Bullet';
import type { EnemyTank } from '../entities/EnemyTank';
import type { PlayerTank } from '../entities/PlayerTank';
import { BULLET_RADIUS, ARENA_HALF } from '../config';

export interface PickupData {
  position: THREE.Vector3;
  radius: number;
  collected: boolean;
  mesh: THREE.Object3D;
}

export class CollisionSystem {
  private readonly box1 = new THREE.Box3();

  /**
   * 检测炮弹是否命中坦克，返回被命中的目标
   */
  checkBulletTankCollisions(
    bullets: Bullet[],
    playerTank: PlayerTank,
    enemyTanks: EnemyTank[],
    obstacles: THREE.Box3[],
  ): { killedEnemies: EnemyTank[]; hitPlayer: boolean; playerDamage: number } {
    const killedEnemies: EnemyTank[] = [];
    let hitPlayer = false;
    let playerDamage = 0;

    for (const bullet of bullets) {
      if (!bullet.alive) continue;

      // 边界检测
      if (Math.abs(bullet.mesh.position.x) > ARENA_HALF + 2 ||
          Math.abs(bullet.mesh.position.z) > ARENA_HALF + 2) {
        bullet.alive = false;
        continue;
      }

      // 掩体碰撞
      const bulletBox = new THREE.Box3(
        new THREE.Vector3(
          bullet.mesh.position.x - BULLET_RADIUS,
          bullet.mesh.position.y - BULLET_RADIUS,
          bullet.mesh.position.z - BULLET_RADIUS,
        ),
        new THREE.Vector3(
          bullet.mesh.position.x + BULLET_RADIUS,
          bullet.mesh.position.y + BULLET_RADIUS,
          bullet.mesh.position.z + BULLET_RADIUS,
        ),
      );

      let hitObstacle = false;
      for (const obs of obstacles) {
        if (bulletBox.intersectsBox(obs)) {
          bullet.alive = false;
          hitObstacle = true;
          break;
        }
      }
      if (hitObstacle) continue;

      // 玩家炮弹 -> 检测敌人
      if (bullet.isPlayerBullet) {
        for (const enemy of enemyTanks) {
          if (!enemy.alive) continue;
          this.box1.setFromObject(enemy.group);
          if (bulletBox.intersectsBox(this.box1)) {
            bullet.alive = false;
            const killed = enemy.takeDamage(bullet.damage);
            if (killed) {
              killedEnemies.push(enemy);
            }
            break;
          }
        }
      } else {
        // 敌人炮弹 -> 检测玩家
        if (playerTank.alive) {
          this.box1.setFromObject(playerTank.group);
          if (bulletBox.intersectsBox(this.box1)) {
            bullet.alive = false;
            hitPlayer = true;
            playerDamage += bullet.damage;
          }
        }
      }
    }

    return { killedEnemies, hitPlayer, playerDamage };
  }

  /**
   * 检测坦克是否与掩体碰撞，返回修正位置
   */
  resolveTankObstacle(
    tankPos: THREE.Vector3,
    tankHalfW: number,
    tankHalfD: number,
    obstacles: THREE.Box3[],
  ): THREE.Vector3 {
    const tankBox = new THREE.Box3(
      new THREE.Vector3(tankPos.x - tankHalfW, 0, tankPos.z - tankHalfD),
      new THREE.Vector3(tankPos.x + tankHalfW, 3, tankPos.z + tankHalfD),
    );

    const pushOut = new THREE.Vector3();
    for (const obs of obstacles) {
      if (tankBox.intersectsBox(obs)) {
        const obsCenter = new THREE.Vector3();
        obs.getCenter(obsCenter);
        const dir = new THREE.Vector3(tankPos.x - obsCenter.x, 0, tankPos.z - obsCenter.z);
        const dist = dir.length();
        if (dist < 0.01) {
          dir.set(1, 0, 0);
        }
        dir.normalize();
        const obsHalf = (obs.max.x - obs.min.x) / 2;
        const penetration = tankHalfW + obsHalf - dist;
        if (penetration > 0) {
          pushOut.addScaledVector(dir, penetration);
        }
      }
    }
    return pushOut;
  }

  /**
   * 检测道具收集
   */
  checkPickups(
    tankPos: THREE.Vector3,
    pickups: PickupData[],
    collectionRadius: number,
  ): PickupData[] {
    const collected: PickupData[] = [];
    for (const pickup of pickups) {
      if (pickup.collected) continue;
      const dist = tankPos.distanceTo(pickup.position);
      if (dist < collectionRadius + pickup.radius) {
        pickup.collected = true;
        collected.push(pickup);
      }
    }
    return collected;
  }
}
